import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import IncrementDecrement from "./IncrementDecrement";
import ClearIcon from "@mui/icons-material/Clear";

const initialRows = [
  { id: 0, player: "", score: 0, bid: 0, result: false },
  { id: 1, player: "", score: 0, bid: 0, result: false },
  { id: 2, player: "", score: 0, bid: 0, result: false },
  { id: 3, player: "", score: 0, bid: 0, result: false },
  { id: 4, player: "", score: 0, bid: 0, result: false },
  { id: 5, player: "", score: 0, bid: 0, result: false },
  { id: 6, player: "", score: 0, bid: 0, result: false },
  { id: 7, player: "", score: 0, bid: 0, result: false },
];

export default function Main() {
  const [rows, setRows] = useState(
    JSON.parse(localStorage.getItem("rows")) || initialRows
  );
  const [roundNumber, setRoundNumber] = useState(
    JSON.parse(localStorage.getItem("roundNumber")) || 1
  );

  const [bidValue, setBidValue] = useState(0);
  const [scoreValue, setScoreValue] = useState(0);
  const [playerField, setPlayerField] = useState("");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  const columns = [
    {
      field: "player",
      headerName: "Player",
      editable: false,
      flex: 5,
      renderCell: (params) => (
        <Typography
          variant="body1"
          style={{
            display: "flex",
            fontSize: "1.5em",
            alignItems: "center",
            height: "100%",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "score",
      headerName: "Score",
      headerAlign: "center",
      align: "center",
      type: "number",
      editable: false,
      flex: 3,
      renderCell: (params) => (
        <Typography
          variant="body1"
          style={{
            display: "flex",
            fontSize: "1.5em",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "bid",
      headerName: "Bid",
      headerAlign: "center",
      align: "center",
      type: "number",
      editable: false,
      flex: 2,
      renderCell: (params) => (
        <Typography
          variant="body1"
          style={{
            display: "flex",
            fontSize: "1.5em",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "result",
      headerName: "Result",
      headerAlign: "center",
      align: "center",
      flex: 3,
      editable: false,
      renderCell: (params) => {
        return (
          <Checkbox
            checked={params.row.result}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
            onChange={(event) => {
              const updatedRows = rows.map((row) => {
                if (row.id === params.row.id) {
                  return { ...row, result: event.target.checked };
                }
                return row;
              });
              setRows(updatedRows);
            }}
          />
        );
      },
    },
  ];

  const selectedRow = (params) => {
    setPlayerField(params.row.player);
    setBidValue(params.row.bid);
    setScoreValue(params.row.score);
    setSelectedRowId(params.row.id);
  };

  const handlePlayerFieldChange = (event) => {
    setPlayerField(event.target.value);
    const updatedRows = rows.map((row) => {
      if (row.id === selectedRowId) {
        return { ...row, player: event.target.value };
      }
      return row;
    });
    setRows(updatedRows);
  };

  const submit = () => {
    if (roundNumber < 25) {
      setRoundNumber((prevRoundNumber) => prevRoundNumber + 1);
      setBidValue(0);
      setRows((prevRows) => {
        return prevRows.map((row, index) => {
          var rowScore = row.score + 10 + row.bid;
          if (row.id === selectedRowId && row.result) {
            setScoreValue(rowScore);
          }
          return {
            ...row,
            score: row.result ? rowScore : row.score,
            bid: 0,
            result: false,
          };
        });
      });
    }
  };

  const clearGame = () => {
    setBidValue(0);
    setScoreValue(0);
    setRoundNumber(1);
    setRows((prevRows) => {
      return prevRows.map((row, index) => {
        return {
          ...row,
          score: 0,
          bid: 0,
          result: false,
        };
      });
    });
  };

  const handleShowAlert = () => {
    setShowAlert(true);
  };

  const handleHideAlert = () => {
    setShowAlert(false);
  };

  useEffect(() => {
    localStorage.setItem("rows", JSON.stringify(rows));
    localStorage.setItem("roundNumber", JSON.stringify(roundNumber));
  }, [rows, roundNumber]);

  return (
    <Box>
      <Stack justifyContent="space-around">
        <Stack
          direction="row"
          justifyContent="space-around"
          sx={{ backgroundColor: "#E0E0E0", paddingBottom: 2, paddingTop: 1 }}
        >
          <Stack sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6">Round</Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", fontSize: 30, lineHeight: 1 }}
            >
              {roundNumber}
            </Typography>
          </Stack>
          <Stack sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6">Cards</Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", fontSize: 30, lineHeight: 1 }}
            >
              {roundNumber <= 13 ? roundNumber : 13 - (roundNumber - 13)}
            </Typography>
          </Stack>
          <Stack sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6">Total Bids</Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", fontSize: 30, lineHeight: 1 }}
            >
              {rows.reduce((sum, row) => sum + parseInt(row.bid), 0)}
            </Typography>
          </Stack>
        </Stack>
        <DataGrid
          rows={rows}
          columns={columns}
          onRowClick={selectedRow}
          disableColumnMenu
          disableColumnResize
          disableColumnSorting
          hideFooter
          rowHeight={50}
          columnHeaderHeight={30}
          pageSize={8} //integer value representing max number of rows
          autoHeight={true}
          onEditCellPropsChangeCommitted={(params, event) => {
            if (params.field === "result" && event.target.type === "checkbox") {
              const updatedRows = rows.map((row) => {
                if (row.id === params.id) {
                  return { ...row, result: event.target.checked };
                }
                return row;
              });
              setRows(updatedRows);
            }
          }}
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "&.MuiDataGrid-root": { border: "none" },
            fontSize: 16,
          }}
        />

        <Stack
          justifyContent="center"
          sx={{
            background: "#E0E0E0",
          }}
        >
          <TextField
            variant="outlined"
            value={playerField}
            onChange={handlePlayerFieldChange}
            onBlur={() => window.scrollTo({ top: -1, behavior: "smooth" })}
            placeholder="Player"
            size="small"
            InputProps={{
              style: { backgroundColor: "white", fontSize: "1.5em" },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() =>
                      handlePlayerFieldChange({ target: { value: "" } })
                    }
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ paddingInline: 1, paddingTop: 1 }}
          />
          <Stack
            direction="row"
            justifyContent={"space-between"}
            sx={{ paddingBottom: 1, paddingInline: 1, fontSize: 20 }}
          >
            <Stack alignItems={"center"}>
              <Typography variant="h6">Score</Typography>
              <IncrementDecrement
                value={scoreValue}
                onChange={(value) => {
                  setScoreValue(value);
                  const updatedRows = rows.map((row) => {
                    if (row.id === selectedRowId) {
                      return { ...row, score: value };
                    }
                    return row;
                  });
                  setRows(updatedRows);
                }}
              />
            </Stack>
            <Stack alignItems="center">
              <Typography variant="h6">Bid</Typography>
              <IncrementDecrement
                value={bidValue}
                onChange={(value) => {
                  setBidValue(value);
                  const updatedRows = rows.map((row) => {
                    if (row.id === selectedRowId) {
                      return { ...row, bid: value };
                    }
                    return row;
                  });
                  setRows(updatedRows);
                }}
              />
            </Stack>
          </Stack>
        </Stack>
        <Stack justifyContent={"space-around"} padding={1} sx={{ height: 150 }}>
          <Button
            variant="contained"
            color="success"
            onClick={submit}
            sx={{ height: 50, fontSize: 20 }}
          >
            Submit Round
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleShowAlert}
            sx={{ height: 50, fontSize: 20 }}
          >
            Reset Game
          </Button>
        </Stack>
      </Stack>
      <Dialog open={showAlert} onClose={handleHideAlert}>
        <DialogTitle id="alert-dialog-title">{"Reset Game?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to reset the game? All game data will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleHideAlert}>Cancel</Button>
          <Button
            onClick={() => {
              handleHideAlert();
              clearGame();
            }}
            autoFocus
            color="error"
          >
            Reset Game
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
